import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Sidebar from './components/Sidebar';

// Optimized QueryClient configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global cache settings
      staleTime: 30000, // 30 seconds - data considered fresh
      gcTime: 5 * 60 * 1000, // 5 minutes - cache retention time
      
      // Retry configuration
      retry: (failureCount: number, error: any) => {
        // Don't retry on 401/403 errors (auth issues)
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        // Don't retry on 404 errors (not found)
        if (error?.response?.status === 404) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Network settings
      networkMode: 'online',
      refetchOnWindowFocus: false, // Prevent excessive refetching
      refetchOnReconnect: true,
      refetchOnMount: true,
      
      // Reduce background refetching for better performance
      refetchInterval: false, // Disable auto-refetch by default
      refetchIntervalInBackground: false,
    },
    mutations: {
      retry: 1, // Retry mutations only once
      networkMode: 'online',
    },
  },
});

function AppContent() {
  const [isSidebarOpen, setSidebarOpen] = React.useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = React.useState(false);

  return (
    <div className="min-h-screen bg-slate-900 text-white font-sans">
      <Router>
        <Sidebar 
          isOpen={isSidebarOpen} 
          setOpen={setSidebarOpen} 
          isCollapsed={isSidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
        <Navbar toggleSidebar={() => setSidebarOpen(!isSidebarOpen)} isCollapsed={isSidebarCollapsed} />
        <main className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-48'}`}>
          <Routes>
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
      </Router>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App; 